import {Injectable, Renderer2, RendererFactory2} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {SnackBarService} from '@q9elements/ui-kit/common';
import {EditorRegistryService} from '../../diagram-render/mx-graph/editor/editor-registry.service';
import {UndoService} from '../../commands/undo.service';
import {ImageCommands} from '../../commands/image/image.commands';
import {resizeImage} from '../../diagram-render/helpers/image.util';
import {DiagramJsonHandlerService} from '../../diagram-data/diagram-json-handler.service';
import {FirebaseDataService} from '../../editor-state/firebase-data.service';
import {SpinnerService} from '../spinner/spinner.service';
import {RequestsService} from '../requests/requests.service';
import {NavigationService} from '../navigation/navigation.service';
import {environment} from '../../../environments/environment';

declare const mxUtils: any;
declare const mxEvent: any;

@Injectable({
  providedIn: 'root'
})
export class ImageUploadService {
  private r2: Renderer2;
  private eventHandlers = {
    dragOver: evt => this.dragoOverListener(evt),
    dragLeave: () => this.dragLeaveListener(),
    dropListener: evt => this.dropListener(evt)
  };

  constructor(
    r2f: RendererFactory2,
    private registry: EditorRegistryService,
    private undoSvc: UndoService,
    private jsonHandlerSvc: DiagramJsonHandlerService,
    private imageCommands: ImageCommands,
    fbDataSvc: FirebaseDataService,
    private snackBarSvc: SnackBarService,
    private translateSvc: TranslateService,
    private spinnerSvc: SpinnerService,
    private reqSvc: RequestsService,
    private navSvc: NavigationService
  ) {
    this.r2 = r2f.createRenderer(null, null);
    fbDataSvc.addService<ImageUploadService>('imageSvc', this);
    this.jsonHandlerSvc.addService<ImageUploadService>('imageSvc', this);
    this.imageCommands.addService<ImageUploadService>('serviceObj.imgSvc', this);
  }

  public enableImages(enable) {
    const graph = this.registry.getGraph();

    if (!this.jsonHandlerSvc.isPro()) {
      return;
    }

    const option = enable ? 'addListener' : 'removeListener';

    mxEvent[option](graph.container, 'dragover', this.eventHandlers.dragOver);

    mxEvent[option](graph.container, 'dragleave', this.eventHandlers.dragLeave);

    mxEvent[option](graph.container, 'drop', this.eventHandlers.dropListener);
  }

  public updateExisting(updateInfo) {
    const graph = this.registry.getGraph();

    if (
      !updateInfo.url ||
      !updateInfo.mxKey ||
      !graph ||
      !graph.isEnabled() ||
      this.navSvc.isNavActive()
    ) {
      return;
    }

    const cell = graph.getModel().getCell(updateInfo.mxKey);

    this.jsonHandlerSvc.update({updateImageUrl: updateInfo});
    let virtual = new Image();

    virtual.onload = function () {
      graph.setCellStyles('image', updateInfo.url, [cell]);
      virtual = null;
    };

    virtual.src = updateInfo.url;
  }

  public createImage(evt: Event) {
    let vInput: HTMLInputElement = this.r2.createElement('input');

    vInput.setAttribute('type', 'file');

    vInput.onchange = (vEvt: Event) => {
      const files = (<HTMLInputElement>vEvt.target).files;

      this.spinnerSvc.show();
      this.handleFiles(files, evt);
      vInput = null;
    };

    vInput.click();
  }

  public uploadImage(url, blob) {
    return this.reqSvc.addToQueue(
      {
        isApi: false,
        url: url,
        data: blob,
        method: 'PUT',
        headers: {
          'Content-Type': blob.type
        }
      },
      true
    );
  }

  public getImageShapeUploadUrl(type): Promise<IUpload> {
    return this.reqSvc.addToQueue(
      {
        isApi: false,
        url: `${environment.API_STATIC_FILE_UPLOAD}/signedurl`,
        queryParams: {
          contentType: type,
          actionType: 'diagramImage'
        },
        method: 'GET'
      },
      true
    );
  }

  public drilldownImageUploadUrl(type): Promise<IUpload> {
    return this.reqSvc.addToQueue(
      {
        isApi: false,
        url: `${environment.API_STATIC_FILE_UPLOAD}/signedurl`,
        queryParams: {
          contentType: type,
          actionType: 'elementsGPTProcess'
        },
        method: 'GET'
      },
      true
    );
  }

  public handleFiles(files, evt) {
    const graph = this.registry.getGraph();

    if (!graph.isEnabled()) {
      this.spinnerSvc.hide();

      return;
    }

    if (files.length > 1) {
      this.spinnerSvc.hide();
      this.snackBarSvc.error(this.translateSvc.instant('POPUPS.ONE_DROP'));

      return;
    }

    const file = files[0];
    const isImage = file && file.type && file.type.substring(0, 5) === 'image';

    if (!isImage) {
      this.spinnerSvc.hide();
      this.snackBarSvc.error(this.translateSvc.instant('POPUPS.IMG_FORMATS'));

      return;
    }

    const pt = mxUtils.convertPoint(
      graph.container,
      mxEvent.getClientX(evt),
      mxEvent.getClientY(evt)
    );
    const tr = graph.view.translate;
    const scale = graph.view.scale;
    const x = pt.x / scale - tr.x;
    const y = pt.y / scale - tr.y;

    const simpleFormats = ['image/png', 'image/jpeg'];
    const index = simpleFormats.indexOf(file.type);

    if (index >= 0) {
      return this.jpgPngHandler(file, simpleFormats[index], x, y);
    } else if (file.type.indexOf('image/gif') >= 0) {
      return this.gifHandler(file, 'image/gif', x, y);
    } else {
      this.spinnerSvc.hide();
      this.snackBarSvc.error(this.translateSvc.instant('POPUPS.IMG_FORMATS'));

      return;
    }
  }

  private dragoOverListener(evt) {
    const graph = this.registry.getGraph();

    if (graph.isEnabled()) {
      graph.container.style.opacity = 0.2;
      evt.stopPropagation();
      evt.preventDefault();
    }
  }

  private dragLeaveListener() {
    const graph = this.registry.getGraph();

    graph.container.style.opacity = 1;
  }

  private dropListener(evt) {
    const graph = this.registry.getGraph();

    evt.stopPropagation();
    evt.preventDefault();
    graph.popupMenuHandler.hideMenu();
    this.spinnerSvc.show();
    graph.container.style.opacity = 1;
    const files = evt.dataTransfer.files;

    this.handleFiles(files, evt);
  }

  private handleCorrupted() {
    this.spinnerSvc.hide();
    this.snackBarSvc.error(this.translateSvc.instant('POPUPS.CORRUPTED'));
  }

  private jpgPngHandler(file, type, x, y) {
    const reader = new FileReader();

    reader.onload = e => {
      const img = new Image();
      const fileData = (<any>e.target).result;

      img.onload = () => {
        const resized = resizeImage(img, type);
        const data = {
          fileData: resized.data,
          fileType: type,
          position: {
            w: Math.max(1, resized.w),
            h: Math.max(1, resized.h),
            x: x - resized.w / 2,
            y: y - resized.h / 2
          },
          link: ''
        };

        const command = this.imageCommands.create(data);

        this.undoSvc.executeCommand(command);
      };

      img.onerror = () => this.handleCorrupted();

      img.src = fileData;
    };

    reader.readAsDataURL(file);
  }

  private gifHandler(file, type, x, y) {
    if (file.size >= 409600) {
      this.spinnerSvc.hide();
      this.snackBarSvc.error(this.translateSvc.instant('POPUPS.GIF_SIZE'));

      return;
    }

    const reader = new FileReader();

    reader.onload = e => {
      const img = new Image();
      const fileData = (<any>e.target).result;

      img.onload = () => {
        const data = {
          fileData: fileData,
          fileType: type,
          position: {
            w: Math.max(1, img.width),
            h: Math.max(1, img.height),
            x: x - img.width / 2,
            y: y - img.height / 2
          },
          link: ''
        };
        const command = this.imageCommands.create(data);

        this.undoSvc.executeCommand(command);
      };

      img.onerror = () => this.handleCorrupted();

      img.src = fileData;
    };

    reader.readAsDataURL(file);
  }
}

export interface IUpload {
  fileStatus: any;
  signedUrl: any;
  key: string;
}
